// src/pages/Admin/Requests/EditRequestModal.jsx
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes } from "react-icons/fa";

// Toolbar Quill
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const isQuillEmpty = (html) => {
  if (!html) return true;
  const text = html.replace(/<(.|\n)*?>/g, "").trim();
  return html === "<p><br></p>" || text.length === 0;
};

const defaultFormFromRequest = (req) => ({
  title: req?.title || "",
  description: req?.description || "",
  priority: req?.priority || "Medium",
});

export default function EditRequestModal({ isOpen, onClose, request, refreshList }) {
  const [form, setForm] = useState(defaultFormFromRequest(request));
  const [loading, setLoading] = useState(false);

  // Sync quand on ouvre / change de requête
  useEffect(() => {
    if (isOpen) setForm(defaultFormFromRequest(request));
  }, [isOpen, request]);

  // Fermer via ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!request?.id) return;

      if (!form.title.trim()) {
        toast.warn("Le titre est requis.");
        return;
      }
      if (isQuillEmpty(form.description)) {
        toast.warn("La description est requise.");
        return;
      }

      setLoading(true);
      try {
        const payload = {
          title: form.title.trim(),
          description: form.description, // HTML Quill
          priority: form.priority,
        };
        await axios.patch(
          `https://api.monappliclub.com/api/requests/updateRequest/${request.id}`,
          payload
        );
        toast.success("Requête mise à jour avec succès !");
        refreshList?.();
        onClose?.();
      } catch (error) {
        console.error("Erreur lors de la mise à jour de la requête :", error);
        toast.error("Erreur lors de la mise à jour de la requête.");
      } finally {
        setLoading(false);
      }
    },
    [form, onClose, refreshList, request?.id]
  );

  if (!isOpen || !request) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay cliquable */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header dégradé + mini notice */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Modifier la requête
          </h3>
          <p className="mt-1 text-white/90 text-xs text-center">
            Ajustez le titre, la description (HTML) et la priorité. Appuyez sur Échap pour fermer.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Titre
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Description
            </label>
            <div className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900">
              {/* Scroll horizontal si la toolbar déborde */}
              <div className="overflow-x-auto">
                <ReactQuill
                  theme="snow"
                  value={form.description}
                  onChange={(val) => setForm((f) => ({ ...f, description: val }))}
                  modules={modules}
                  formats={formats}
                  className="min-h-[180px]"
                  placeholder="Détaillez la demande, contexte, étapes, pièces jointes éventuelles…"
                />
              </div>
            </div>
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Conseil : indiquez les étapes pour reproduire et l’environnement (navigateur/appareil).
            </p>
          </div>

          {/* Priorité */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Priorité
            </label>
            <select
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              <option value="High">Urgent</option>
              <option value="Medium">Moyenne</option>
              <option value="Low">Basse</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
              disabled={loading}
            />
            <Button
              text={loading ? "En cours…" : "Modifier"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl gap-2 ${loading ? "opacity-60 pointer-events-none" : ""}`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
